import { useAuth } from "@/contexts/AuthContext";
import { Priority } from "@/types";
import { useMemo } from "react";

export interface PriorityEditPermission {
    /** Whether the current user may change task-level fields: details, status, assignees, DRI. */
    canEditTask: boolean;
    /**
     * Whether the current user has an assignment row of their own on this task. A non-owner with a row
     * may still edit that row (status, IC task, blocker) via `PATCH /priorities/:pid/assignments/me`.
     */
    canEditOwnRow: boolean;
    /** The DRI's display name, for explaining a refusal before the user hits save. */
    driName?: string;
    /** True when the task has no DRI, so nobody but its creator or an admin owns it. */
    isDriMissing: boolean;
}

/**
 * Mirrors the backend's task-level authorization (§3.2) so the form can go read-only instead of
 * collecting edits it will refuse on save.
 *
 * This is presentation only — the domain enforces it regardless of what the client renders. The reason
 * to duplicate it is that `PUT /priorities/:pid` is refused *whole*: a form that looks editable is a
 * trap. The split matters because the backend has two grants, not one — task-level (DRI/creator/admin)
 * and own-row (`PATCH /priorities/:pid/assignments/me`) — so the form must lock the task fields while
 * leaving the caller's own assignment row editable.
 *
 * A DRI is only a row flagged `isDri`. A lone assignee on a candidate is not an implicit owner: the
 * backend answers `PRIORITY_CANDIDATE_MUST_CLAIM` and expects them to claim it first.
 */
export function usePriorityEditPermission(initialData?: Partial<Priority>): PriorityEditPermission {
    const { user, isAdmin } = useAuth();

    return useMemo(() => {
        // Create mode: there is nothing to be permissioned against yet — the row does not exist.
        if (!initialData?.pid) return { canEditTask: true, canEditOwnRow: true, isDriMissing: false };

        const assignments = initialData.assignments ?? [];
        const dri = assignments.find((a) => a.isDri);

        const isDri = Boolean(dri && user?.pid && dri.assignedUser.pid === user.pid);
        const canEditTask = isDri || Boolean(initialData.isCreator) || isAdmin;
        const canEditOwnRow = Boolean(
            user?.pid && assignments.some((a) => a.assignedUser.pid === user.pid)
        );

        return {
            canEditTask,
            canEditOwnRow,
            driName: dri?.assignedUser.fullName,
            isDriMissing: !dri,
        };
    }, [initialData?.pid, initialData?.assignments, initialData?.isCreator, user?.pid, isAdmin]);
}
